import { useUserSession } from '@solinkcorp/authentication';
import { type FeatureTabEntry } from '@solinkcorp/event-standard/lib/src/types';
import { toast } from '@solinkcorp/solink-components';
import CloudAiApi from 'clients/CloudAiApi';
import { type EventClientQueryParameters } from 'clients/EventClient';
import { type IEvent } from 'clients/PlatformApi';
import { CommonContext } from 'components/Contexts';
import React, { useContext } from 'react';
import useLocale from 'src/hooks/useLocale';
import { useSolinkEvents } from 'src/hooks/useSolinkEvents';
import { useFeatureStandard } from 'utils/EventStandardCache/eventStandardCache';
import { type CamerasDictionaryCamera } from 'utils/types';
import { DAY_IN_MILLISECONDS } from 'vd-constants';
import { TIME_TARGETS, type UseSpotCheckForm } from './useSpotCheckForm';

interface ReportMetadata {
  mergeWithSubtypes?: string[];
  categories?: string[];
}

interface ExtractedQueryParameters {
  include?: string;
  filter?: {
    regexp: {
      subtype: {
        value: string;
      };
    };
  };
}

export const useEventSpotCheckPreview = (spotCheckForm: UseSpotCheckForm) => {
  const user = useUserSession();
  const locale = useLocale();
  const { camerasDictionary } = useContext(CommonContext);
  const featureStandard = useFeatureStandard();

  const [isEventSpotCheckFormTouched, setIsEventSpotCheckFormTouched] =
    React.useState<boolean>(false);
  const [eventImagePreviewUrls, setEventImagePreviewUrls] = React.useState<string[]>([]);
  const [sentEventSpotCheckRequest, setSentEventSpotCheckRequest] = React.useState(false);
  const [eventSpotCheckAnswer, setEventSpotCheckAnswer] = React.useState<string>('');
  const [reportEvent, setReportEvent] = React.useState<IEvent | undefined>();

  const sendEventSpotCheckPreview = async (
    snapshotUrls: string[],
    reportEvent: IEvent | undefined,
    cameras: CamerasDictionaryCamera[],
  ) => {
    const jwt = user.accessToken;
    if (!sentEventSpotCheckRequest) {
      setSentEventSpotCheckRequest(true);
      try {
        if (snapshotUrls.length) {
          const result = await CloudAiApi.getEventSpotCheckPreview(jwt, {
            prompt: spotCheckForm.aiChatPrompt,
            responseFormat: spotCheckForm.aiChatAnswerFormat ?? '',
            eventMetadata: reportEvent?.details,
            images: snapshotUrls,
            cameras,
            states: spotCheckForm.aiChatAnswerStates.map(({ name, description }) => ({
              name,
              description,
            })),
            timeTarget: spotCheckForm.eventTimeTarget,
            timestampOffset: spotCheckForm.eventTimestampOffset,
          });
          setEventSpotCheckAnswer(JSON.stringify(result, null, 2));
        } else {
          setIsEventSpotCheckFormTouched(true);
        }
      } catch (_error) {
        toast.error(locale.scheduledAIChat.eventSpotCheckPreviewFailure);
        setIsEventSpotCheckFormTouched(true);
      } finally {
        setSentEventSpotCheckRequest(false);
      }
    }
  };

  const dateRangeLastThirtyDays = React.useMemo(() => {
    const currentTime = Date.now();
    return {
      start: currentTime - 30 * DAY_IN_MILLISECONDS,
      end: currentTime,
    };
  }, []);

  /**
   * This function extracts query parameters for query the report events
   * @param tab
   * @param reportMetadata
   * @returns query parameters
   */
  const extractQueryParameters = (
    tab: FeatureTabEntry,
    reportMetadata: ReportMetadata,
  ): ExtractedQueryParameters => {
    const subtypeSet = new Set<string>();

    // Add base subtypes from tab.filter
    if (tab.filter.eventSubtypeQuery) {
      subtypeSet.add(tab.filter.eventSubtypeQuery);
    }

    // Add mergeWith subtypes from tab.eventSubtypes
    if (tab.eventSubtypes) {
      Object.values(tab.eventSubtypes).forEach(({ mergeWith }) => {
        if (mergeWith) {
          mergeWith.forEach((item) => subtypeSet.add(item));
        }
      });
    }

    // Add from report metadata
    reportMetadata.mergeWithSubtypes?.forEach((subtype) => subtypeSet.add(subtype));

    // Build RegExp pattern (match any of the subtypes)
    const regexPattern = Array.from(subtypeSet).join('|');

    return {
      include: tab.forceInclude?.tags?.eventsQuery,
      filter: {
        regexp: {
          subtype: {
            value: regexPattern,
          },
        },
      },
    };
  };

  const reportEventQuery = React.useMemo(() => {
    const metadata = spotCheckForm?.selectedEventReport?.metadata;
    const categories: string[] = metadata?.categories;

    let queryParams: ExtractedQueryParameters = {};
    if (categories && featureStandard?.tabs[categories[0]] && metadata) {
      queryParams = extractQueryParameters(featureStandard?.tabs[categories[0]], metadata);
    }

    const query: EventClientQueryParameters = {
      ...spotCheckForm.selectedEventReport?.query,
    };

    if (queryParams?.filter) {
      query.eqFilter = queryParams.filter;
    }
    if (queryParams?.include) {
      query.include = queryParams.include;
    }

    return query;
  }, [spotCheckForm?.selectedEventReport, featureStandard]);

  const eventResultsFromReport = useSolinkEvents(
    {
      ...reportEventQuery,
      selectedReportId: spotCheckForm.selectedEventReport?.templateId,
    },
    dateRangeLastThirtyDays,
    { limitTotal: false },
  );

  const [newEvent, reportCameras] = React.useMemo(() => {
    const newEvent = eventResultsFromReport.events?.find((event) => event.cameras.length > 0);
    return [newEvent, newEvent?.cameras?.filter((cam) => cam?.streams.length) || []];
  }, [eventResultsFromReport]);

  const totalEventsInLast30Days = React.useMemo(() => {
    return spotCheckForm.selectedEventReport ? (eventResultsFromReport.total ?? 0) : 0;
  }, [eventResultsFromReport, spotCheckForm.selectedEventReport]);

  const calculateTotalEventTime = React.useCallback(
    (eventTime: number) => {
      if (!eventTime) {
        return spotCheckForm.eventTimestampOffset * 1000;
      }
      return eventTime + spotCheckForm.eventTimestampOffset * 1000;
    },
    [spotCheckForm.eventTimestampOffset],
  );

  const handleEventSpotCheckPreview = async () => {
    setIsEventSpotCheckFormTouched(false);
    if (!spotCheckForm.selectedEventReport) {
      return;
    }

    let totalTime = 0;
    if (newEvent) {
      if (spotCheckForm.eventTimeTarget === TIME_TARGETS.START && newEvent?.startTime) {
        totalTime = calculateTotalEventTime(newEvent?.startTime.getTime());
      } else if (spotCheckForm.eventTimeTarget === TIME_TARGETS.END && newEvent?.endTime) {
        totalTime = calculateTotalEventTime(newEvent?.endTime.getTime());
      } else {
        totalTime = calculateTotalEventTime(new Date(newEvent?.createTime).getTime());
      }
    }

    const snapshotUrls = await Promise.all(
      reportCameras.map(async (cam) => {
        if (!cam?.chId) return null;

        const camera = camerasDictionary[cam.chId];
        if (!camera) return null;

        // Fetch the accessible address for the device
        const deviceUrl = camera?.deviceUrl;
        const camHDStream = cam?.streams.find((stream) => stream?.name === 'HD');

        // Create the base url
        const baseUrl = `${deviceUrl}/vms/cameras/${cam.id}/snapshot?time=${totalTime}&precision=1&jwt=${user.accessToken}`;

        // Try HD stream for snapshot first
        if (camHDStream?.id) {
          const hdUrl = `${baseUrl}&stream=${camHDStream.id}`;

          // If HD stream can generate snapshot use it
          if (await isImageUrl(hdUrl)) return { url: hdUrl, camera };
        }

        // Fallback to non-HD stream to generate snapshot
        if (await isImageUrl(baseUrl)) return { url: baseUrl, camera };
        // if neither url contains an image
        return null;
      }),
    );

    const validUrls = snapshotUrls.filter(
      (item): item is { url: string; camera: CamerasDictionaryCamera } =>
        item !== null && typeof item.url === 'string',
    );
    if (validUrls.length) {
      setEventImagePreviewUrls(validUrls.map(({ url }) => url));
      setReportEvent(newEvent);
    } else {
      toast.error(locale.scheduledAIChat.eventSpotCheckPreviewSnapshotFailure);
    }

    sendEventSpotCheckPreview(
      validUrls.map(({ url }) => url),
      newEvent,
      validUrls.map(({ camera }) => camera),
    );
  };

  // Check if url contains an image
  const isImageUrl = async (url: string): Promise<boolean> => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      const contentType = response.headers.get('Content-Type');
      return contentType?.startsWith('image/') ?? false;
    } catch (_error) {
      return false;
    }
  };

  React.useEffect(() => {
    if (!spotCheckForm.selectedEventReport) {
      setIsEventSpotCheckFormTouched(false);
    } else {
      setIsEventSpotCheckFormTouched(true);
    }
  }, [
    spotCheckForm.aiChatAnswerFormat,
    spotCheckForm.aiChatPrompt,
    spotCheckForm.eventTimestampOffset,
    spotCheckForm.eventTimeTarget,
    spotCheckForm.selectedEventReport,
    spotCheckForm.title,
  ]);

  return {
    isEventSpotCheckFormTouched,
    eventImagePreviewUrls,
    sentEventSpotCheckRequest,
    eventSpotCheckAnswer,
    totalEventsInLast30Days,
    reportEvent,
    handleEventSpotCheckPreview,
    setIsEventSpotCheckFormTouched,
    setEventImagePreviewUrls,
    setSentEventSpotCheckRequest,
    setEventSpotCheckAnswer,
  };
};
